import { consola } from 'consola'
import { AuthLogoutService, LogoutReason, UserType } from '~/features/auth/lib/AuthLogoutService'

const logger = consola.withTag('app-reset')

/**
 * Типы данных которые НЕ нужно очищать при сбросе
 */
const PROTECTED_STORAGE_KEYS = [
  'appLanguage',
  'cookieConsent',
  'pushNotificationPermission',
  'themePreference',
  'debugLogs',
] as const

/**
 * Опции для сброса приложения
 */
export interface AppResetOptions {
  /** Разлогинить пользователя */
  logout?: boolean
  /** Очистить все stores */
  clearStores?: boolean
  /** Очистить localStorage (кроме защищенных ключей) */
  clearLocalStorage?: boolean
  /** Очистить SecureStorage на нативных платформах */
  clearSecureStorage?: boolean
  /** Очистить кэш OfflineManager */
  clearCache?: boolean
  /** Причина сброса (для логирования) */
  reason?: string
  /** Тихий сброс без уведомлений */
  silent?: boolean
  /** Редирект после сброса */
  redirectTo?: string
}

/**
 * Composable для полного сброса состояния приложения
 *
 * ✅ Используется при критических обновлениях
 * ✅ Следует принципам Single Responsibility
 * ✅ Централизованное управление сбросом
 *
 * @example
 * const { performFullReset, clearAllStores } = useAppReset()
 * await performFullReset({ reason: 'major_update', logout: true })
 */
export const useAppReset = () => {
  /**
   * Выполняет полный сброс приложения
   */
  const performFullReset = async (options: AppResetOptions = {}): Promise<void> => {
    const {
      logout = true,
      clearStores = true,
      clearLocalStorage = true,
      clearSecureStorage = true,
      clearCache = true,
      reason = 'unknown',
      silent = false,
      redirectTo = '/',
    } = options

    logger.warn('🔄 Начинаем полный сброс приложения', {
      reason,
      logout,
      clearStores,
      clearLocalStorage,
      clearSecureStorage,
      clearCache,
      timestamp: new Date().toISOString(),
    })

    try {
      // 1. Очищаем Pinia stores
      if (clearStores) {
        await clearAllStores()
      }

      // 2. Очищаем кэш OfflineManager
      if (clearCache) {
        await clearOfflineCache()
      }

      // 3. Очищаем localStorage (кроме защищенных ключей)
      if (clearLocalStorage) {
        await clearLocalStorageSafely()
      }

      // 4. Очищаем SecureStorage на нативных платформах
      if (clearSecureStorage) {
        await clearNativeSecureStorage()
      }

      // 5. Разлогиниваем пользователя
      if (logout) {
        await performLogout(reason, silent, redirectTo)
      } else if (redirectTo !== '/' && // Редирект без разлогинивания
        process.client) {
          window.location.href = redirectTo
        }

      logger.success('✅ Полный сброс приложения завершен')
    } catch (error) {
      logger.error('❌ Ошибка при сбросе приложения:', error)

      // Критичный fallback - пытаемся хотя бы разлогинить
      if (logout) {
        try {
          await performLogout(reason, true, redirectTo)
        } catch (fallbackError) {
          logger.error('❌ Критическая ошибка fallback:', fallbackError)
        }
      }

      throw error
    }
  }

  /**
   * Очищает все Pinia stores
   */
  const clearAllStores = async (): Promise<void> => {
    logger.debug('🧹 Очистка Pinia stores...')

    try {
      // Последовательные import(): параллельный Promise.allSettled здесь ловит deadlock
      // в средах с циклическими зависимостями / тестовыми моками ESM.
      const loaders = [
        () => import('@entities/order').then(m => m.useCartStore()),
        () => import('@entities/user').then(m => m.useUserStore()),
        () => import('@entities/user').then(m => m.useUserProfileStore()),
        () => import('~/features/basket/@x/auth').then(m => m.useBasketState()),
        () => import('~/features/basket/@x/auth').then(m => m.useCheckoutFormStore()),
        () => import('@entities/institution').then(m => m.useInstitutionStore()),
        () => import('@shared/model').then(m => m.useModalStore()),
        () => import('~/features/payment/@x/auth').then(m => m.usePaymentStore()),
        () => import('~/features/payment/@x/auth').then(m => m.useUnpaidOrdersStore()),
        () => import('@entities/loyalty').then(m => m.useLoyaltyStore()),
        () => import('@entities/notification').then(m => m.useNotificationsStore()),
        () => import('@shared/model').then(m => m.useAlertStore()),
      ] as const

      const stores: PromiseSettledResult<unknown>[] = []
      for (const load of loaders) {
        try {
          stores.push({ status: 'fulfilled', value: await load() })
        } catch (error) {
          stores.push({ status: 'rejected', reason: error })
        }
      }

      // Вызываем $reset() на каждом store если он есть
      for (const [index, result] of stores.entries()) {
        if (result.status === 'fulfilled' && result.value) {
          try {
            const store = result.value as any
            if (typeof store.$reset === 'function') {
              store.$reset()
              logger.debug(`✅ Store #${index} сброшен`)
            } else if (typeof store.clearCart === 'function') {
              // Для cart store используем clearCart
              store.clearCart()
              logger.debug(`✅ Cart store очищен`)
            } else if (typeof store.clearUser === 'function') {
              // Для user stores используем clearUser
              store.clearUser()
              logger.debug(`✅ User store очищен`)
            }
          } catch (error) {
            logger.warn(`⚠️ Ошибка сброса store #${index}:`, error)
          }
        }
      }

      logger.success('✅ Все Pinia stores очищены')
    /* v8 ignore start */
    } catch (error) {
      logger.error('❌ Ошибка очистки stores:', error)
      throw error
    }
    /* v8 ignore stop */
  }

  /**
   * Очищает кэш OfflineManager
   */
  const clearOfflineCache = async (): Promise<void> => {
    logger.debug('🧹 Очистка offline кэша...')

    try {
      const { offlineManager } = await import('@shared/lib/networking')
      offlineManager.clearCache()
      logger.success('✅ Offline кэш очищен')
    } catch (error) {
      logger.warn('⚠️ Ошибка очистки offline кэша:', error)
    }
  }

  /**
   * Безопасно очищает localStorage, сохраняя защищенные ключи
   */
  const clearLocalStorageSafely = (): void => {
    /* v8 ignore next */
    if (!process.client) return

    logger.debug('🧹 Очистка localStorage...')

    try {
      // Сохраняем защищенные данные
      const protectedData: Record<string, string | null> = {}
      for (const key of PROTECTED_STORAGE_KEYS) {
        const value = localStorage.getItem(key)
        if (value !== null) {
          protectedData[key] = value
        }
      }

      // Получаем все ключи для логирования
      const allKeys = Object.keys(localStorage)
      const keysToRemove = allKeys.filter(
        key => !PROTECTED_STORAGE_KEYS.includes(key as any),
      )

      logger.debug('📋 localStorage очистка:', {
        totalKeys: allKeys.length,
        keysToRemove: keysToRemove.length,
        protectedKeys: Object.keys(protectedData).length,
        removing: keysToRemove.slice(0, 10), // Первые 10 для примера
      })

      // Очищаем весь localStorage
      localStorage.clear()

      // Восстанавливаем защищенные данные
      for (const [key, value] of Object.entries(protectedData)) {
        if (value !== null) {
          localStorage.setItem(key, value)
        }
      }

      logger.success('✅ localStorage очищен (защищенные ключи сохранены)', {
        protected: Object.keys(protectedData),
      })
    } catch (error) {
      logger.error('❌ Ошибка очистки localStorage:', error)
      throw error
    }
  }

  /**
   * Очищает SecureStorage на нативных платформах
   */
  const clearNativeSecureStorage = async (): Promise<void> => {
    /* v8 ignore next */
    if (!process.client) return

    try {
      const { Capacitor } = await import('@capacitor/core')

      if (!Capacitor.isNativePlatform()) {
        logger.debug('ℹ️ Не нативная платформа, пропускаем SecureStorage')
        return
      }

      logger.debug('🧹 Очистка SecureStorage...')

      const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin')

      // Список ключей для очистки
      const keysToRemove = [
        'authAccessToken',
        'authRefreshToken',
        'authSessionId',
        'authAdminAccessToken',
        'authAdminRefreshToken',
        'offline_user',
        'offline_tokens',
        'offline_pending_requests',
      ]

      await Promise.allSettled(
        keysToRemove.map(key =>
          SecureStoragePlugin.remove({ key }).catch(_error => {
            logger.debug(`ℹ️ Ключ ${key} не найден в SecureStorage`)
          }),
        ),
      )

      logger.success('✅ SecureStorage очищен')
    } catch (error) {
      logger.warn('⚠️ Ошибка очистки SecureStorage:', error)
    }
  }

  /**
   * Выполняет разлогинивание через AuthLogoutService
   */
  const performLogout = async (
    reason: string,
    silent: boolean,
    redirectTo: string,
  ): Promise<void> => {
    logger.debug('🚪 Выполняем разлогинивание...')

    try {
      await AuthLogoutService.logout({
        reason: LogoutReason.SECURITY_ISSUE,
        userType: UserType.REGULAR,
        redirectTo,
        silent,
        metadata: {
          source: 'useAppReset.performLogout',
          resetReason: reason,
        },
      })
    } catch (error) {
      logger.error('❌ Ошибка разлогинивания:', error)
      throw error
    }
  }

  /**
   * Частичный сброс - только stores без разлогинивания
   */
  const performPartialReset = async (): Promise<void> => {
    logger.info('🔄 Частичный сброс (без разлогинивания)')

    await performFullReset({
      logout: false,
      clearStores: true,
      clearLocalStorage: false,
      clearSecureStorage: false,
      clearCache: true,
      reason: 'partial_reset',
    })
  }

  /**
   * Сброс только кэша без разлогинивания
   */
  const performCacheReset = async (): Promise<void> => {
    logger.info('🔄 Сброс кэша')

    await performFullReset({
      logout: false,
      clearStores: false,
      clearLocalStorage: false,
      clearSecureStorage: false,
      clearCache: true,
      reason: 'cache_reset',
    })
  }

  return {
    // Основные методы
    performFullReset,
    performPartialReset,
    performCacheReset,

    // Утилиты для отдельных операций
    clearAllStores,
    clearOfflineCache,
    clearLocalStorageSafely,
    clearNativeSecureStorage,
  }
}

